'use client';

import { usePersistentArray } from '../../reducers/usePersistentReducer';
import { useState } from 'react';
import { toast } from 'sonner';
import { mutate } from 'swr';
import { showBadgeToast } from '../ui/BadgeToast';

export default function LogPracticeButton({ rootNote, scaleType, isGuest, userId, ...props }) {
  const [isLogging, setIsLogging] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [practiceLog, logOperations] = usePersistentArray([], 'scalePracticerLog');

  const handleLogPractice = async () => {
    setIsLogging(true);
    const now = new Date();

    // Add practice session to local log
    const newSession = {
      id: now.getTime(),
      timestamp: now.toISOString(),
      rootNote,
      scaleType,
      date: now.toLocaleDateString(),
      time: now.toLocaleTimeString(),
    };
    
    logOperations.add(newSession);

    try {
      // Save to database via API
      if (!isGuest) {
        const response = await fetch('/api/practice-session', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            rootNote,
            scaleType,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to save practice session');
        }

        const data = await response.json();

        // Revalidate the SWR cache to update the DailyPracticeLogDisplay
        await mutate('/api/practice-session');

        // Handle badge notifications
        if (data.badges?.newBadges?.length > 0) {
          for (const badge of data.badges.newBadges) {
            showBadgeToast({
              image: badge.iconUrl,
              title: 'New Badge Earned!',
              description: `You earned the ${badge.name} badge!`,
              profileLink: `/profile/${userId}`
            });
          }
        }

        
        if (data.isDuplicate) {
          toast.info(data.message);
        } else {
          toast.success(data.message);
        }
      }
    } catch (error) {
      console.error('Error logging practice:', error);
      toast.error('Failed to log practice session');
    } finally {
      // Show success message briefly
      setTimeout(() => {
        setIsLogging(false);
      }, 1500);
    }
  }; 

  // Get today's practice 
  // const todaysPracticeCount = practiceLog.filter((session) => {
  //   const today = new Date().toLocaleDateString();
  //   return session.date === today && session.rootNote === rootNote && session.scaleType === scaleType;
  // }).length;

  return (
    <div className="self-center pt-8 inline-flex flex-col items-center gap-2">
      {isGuest ? (
        <a href="/signin" className="btn btn-secondary gap-2 min-w-[200px]" {...props}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
            <path
              fillRule="evenodd"
              d="M3 4.25A2.25 2.25 0 015.25 2h5.5A2.25 2.25 0 0113 4.25v2a.75.75 0 01-1.5 0v-2a.75.75 0 00-.75-.75h-5.5a.75.75 0 00-.75.75v11.5c0 .414.336.75.75.75h5.5a.75.75 0 00.75-.75v-2a.75.75 0 011.5 0v2A2.25 2.25 0 0110.75 18h-5.5A2.25 2.25 0 013 15.75V4.25z"
              clipRule="evenodd"
            />
            <path
              fillRule="evenodd"
              d="M19 10a.75.75 0 00-.75-.75H8.704l1.048-.943a.75.75 0 10-1.004-1.114l-2.5 2.25a.75.75 0 000 1.114l2.5 2.25a.75.75 0 101.004-1.114l-1.048-.943h9.546A.75.75 0 0019 10z"
              clipRule="evenodd"
            />
          </svg>
          Log in to track your practice
        </a>
      ) : (
        <button
          onClick={handleLogPractice}
          className={`btn btn-primary gap-2 min-w-[200px] ${isLogging ? 'btn-success' : ''}`}
          disabled={isLogging}
          {...props}
        >
          {isLogging ? (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                <path
                  fillRule="evenodd"
                  d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                  clipRule="evenodd"
                />
              </svg>
              Practice Logged!
            </>
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                <path d="M15.5 2A1.5 1.5 0 0014 3.5v13a1.5 1.5 0 001.5 1.5h1a1.5 1.5 0 001.5-1.5v-13A1.5 1.5 0 0016.5 2h-1zM9.5 6A1.5 1.5 0 008 7.5v9A1.5 1.5 0 009.5 18h1a1.5 1.5 0 001.5-1.5v-9A1.5 1.5 0 0010.5 6h-1zM3.5 10A1.5 1.5 0 002 11.5v5A1.5 1.5 0 003.5 18h1A1.5 1.5 0 006 16.5v-5A1.5 1.5 0 004.5 10h-1z" />
              </svg>
              Log Practice
            </>
          )}
        </button>
      )}

      {/* Keyboard Shortcut Hint */}
      {/* {!isGuest && (
        <div className="text-xs text-base-content/60">
          Press <kbd className="kbd kbd-xs">Space</kbd> to quickly log practice
        </div>
      )} */}
    </div>
  );
}
